import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { FilePickerWithProgress } from '@bingads-webui-react/file-picker-with-progress';
import { useUrlBuilder } from '@bingads-webui-cc-react/url-builder-react';
import { CertMaxSizeLimitation, ApiFileUrlBuilderName, FileAllowedExtension, TaxFormMode, DeclineReasonTypesI18nMapping, TaxCertificationStatusInfoI18nMap } from '../constants';
import { TaxCertificationShape } from '../shapes';
import { getTaxFormsStyles } from '../styles';
import { useFilePickerProgressI18n } from '../hooks/use-i18n';

const { VatStatusEnum } = Constants;

export const TaxCertificateFilePicker = ({
  i18n,
  customStrings,
  certifications,
  mtTaxCertification,
  onCertificationsChange,
  taxFormMode,
  showLineByLineFormGroup,
  alwaysShowError,
  disabled,
  required,
  urlBuilder,
  showTaxCertLink,
  taxCertLabel,
  helpPopupTopic,
  additionalFormData,
  isGlobalTaxForm,
}) => {
  const filePickerProgressI18n = useFilePickerProgressI18n(i18n);
  const [taxCertFileState, setTaxCertFileState] = useState({
    fileList: certifications || [],
    isValid: false,
  });
  const initialFilesUploadCompletedInfo = useMemo(() => _.reduce(certifications, (prev, curr) => {
    if (curr && curr.id && curr.guid) {
    // eslint-disable-next-line no-param-reassign
      prev[curr.id] = {
        FileGuid: curr.guid,
        success: true,
      };
    }
    return prev;
  }, {}), [certifications]);
  const classes = useDefaultStyles(getTaxFormsStyles);

  const isEditMode = taxFormMode === TaxFormMode.EditMode;
  const taxCertObj = mtTaxCertification.taxCertifications[0];
  const certGuid = _.get(taxCertObj, 'guid');
  const certName = _.get(taxCertObj, 'displayName');
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;

  // TODO: [Task 3524668] remove the urlBuilder props after wrapping all TaxFroms with UrlBuilderContextProvider
  const getApiFileUrlBuilderFromProp = useCallback((urlBuilderProp, config) => {
    const UrlBuilders = _.get(urlBuilderProp, 'UrlBuilders');
    return UrlBuilders && _.isFunction(UrlBuilders[ApiFileUrlBuilderName]) ? new UrlBuilders[ApiFileUrlBuilderName](config) : {};
  }, []);
  let apiFileUrlBuilder = useUrlBuilder(ApiFileUrlBuilderName);
  apiFileUrlBuilder = !_.isEmpty(apiFileUrlBuilder) ? apiFileUrlBuilder : getApiFileUrlBuilderFromProp(urlBuilder);
  const downloadFileUrl = useCallback(({ FileGuid }) => apiFileUrlBuilder.params({ FileGuid, ...additionalFormData }).url, [additionalFormData, apiFileUrlBuilder]);

  const fileValidator = useCallback(file => _.result(file, 'size', null) > CertMaxSizeLimitation && i18n.getString(_TL_('The file you’ve uploaded is too large. Please reduce the file size to less than 20 megabytes and try again.')), [i18n]);

  const onFileCompletedInfoChange = useCallback((taxCertFileCompletedInfo) => {
    const certificationsWithGuid = taxCertFileState.fileList.map(file => ((taxCertFileCompletedInfo[file.id] && taxCertFileCompletedInfo[file.id].success)
      ? { ...file, guid: taxCertFileCompletedInfo[file.id].FileGuid }
      : { ...file, guid: null }
    ));
    onCertificationsChange({
      certifications: certificationsWithGuid,
      isValid: taxCertFileState.isValid,
    });
  }, [onCertificationsChange, taxCertFileState]);

  const onFileChange = useCallback(({ fileList, isValid }) => {
    setTaxCertFileState({
      fileList,
      isValid,
    });
    onCertificationsChange({
      certifications: fileList,
      isValid,
    });
  }, [onCertificationsChange]);

  const taxCertStatusText = useMemo(() => {
    const {
      taxCertifications,
      status: mtTaxCertStatus,
      declineReasonType,
    } = mtTaxCertification;

    const isMTStatusInvalid = mtTaxCertStatus === VatStatusEnum.Invalid;
    const certStatusClassName = classNames('cert-validation-status', isMTStatusInvalid && classes.invalidCertValidationStatus);
    const getCertStatusNode = content => <div className={certStatusClassName}>{content}</div>;

    if (!_.isEmpty(taxCertifications) && isGlobalTaxForm) {
      return <div className="cert-uploaded-status">{i18n.getString(_TL_('Your tax certificate has been uploaded.'))}</div>;
    }

    // todo: It is considered to refactor in an enumerated manner when tax status text becomes more complicated.
    if (mtTaxCertStatus && !isGlobalTaxForm) {
      const status = _.has(TaxCertificationStatusInfoI18nMap, mtTaxCertStatus) && i18n.getString(TaxCertificationStatusInfoI18nMap[mtTaxCertStatus]);
      const declineReason = isMTStatusInvalid && DeclineReasonTypesI18nMapping[declineReasonType] && i18n.getString(DeclineReasonTypesI18nMapping[declineReasonType]);

      return getCertStatusNode(_.compact([status, declineReason]).join(' '));
    }

    if (!isEditMode) {
      if (!_.isEmpty(taxCertifications) && certName) {
        return getCertStatusNode(certName);
      }
      return getCertStatusNode(i18n.getString(_TL_('(Not set)')));
    }

    return null;
  }, [mtTaxCertification, classes.invalidCertValidationStatus, isGlobalTaxForm, isEditMode, i18n, certName]);

  const certDownloadLink = useMemo(() => {
    const isCertPendingReview = mtTaxCertification.status === VatStatusEnum.Pending;
    const mtDownloadLink = showTaxCertLink
      && isCertPendingReview
      && certGuid
      && !_.isEmpty(apiFileUrlBuilder)
      && downloadFileUrl({ FileGuid: certGuid });

    return mtDownloadLink && !isGlobalTaxForm && (
      <a
        className={classNames('download-cert-link', classes.downloadCertLink)}
        href={mtDownloadLink}
      >
        {i18n.getString(_TL_('view the certificate'))}
      </a>
    );
  }, [mtTaxCertification.status, showTaxCertLink, certGuid, apiFileUrlBuilder, downloadFileUrl, isGlobalTaxForm, classes.downloadCertLink, i18n]);

  return (
    <CustomizedFormGroup
      isStatic
      label={taxCertLabel}
      className="tax-cert-file-picker-form-group"
      labelColExtraClass={classes.taxLabelColClass}
      labelClass={classes.taxCertificateLabel}
      customLabelSuffix={certDownloadLink}
      helpPopupTopic={helpPopupTopic}
    >
      {taxCertStatusText}
      {isEditMode && (
        <FilePickerWithProgress
          onFileCompletedInfoChange={onFileCompletedInfoChange}
          endPoint={apiFileUrlBuilder.url || ''}
          className="tax-cert-file-picker"
          i18n={filePickerProgressI18n}
          fileList={taxCertFileState.fileList || []}
          initialFilesUploadCompletedInfo={initialFilesUploadCompletedInfo}
          customStrings={customStrings}
          onFileChange={onFileChange}
          allowedFileExtensions={FileAllowedExtension}
          validator={fileValidator}
          maxCount={1}
          alwaysShowError={alwaysShowError}
          additionalFormData={additionalFormData}
          disabled={disabled}
          required={required}
        />
      )}
    </CustomizedFormGroup>
  );
};

TaxCertificateFilePicker.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  // customStrings contain the custom i18n strings, keys are the same with i18n keys.
  // Right now, only FilePicker is supported, feel free to expand the usage to the whole file.
  customStrings: PropTypes.shape({}),
  certifications: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    displayName: PropTypes.string.isRequired,
    file: PropTypes.instanceOf(File),
    isValid: PropTypes.bool,
  })),
  mtTaxCertification: TaxCertificationShape,
  onCertificationsChange: PropTypes.func,
  taxFormMode: PropTypes.string,
  showLineByLineFormGroup: PropTypes.bool,
  alwaysShowError: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  urlBuilder: PropTypes.shape({
    UrlBuilders: PropTypes.shape({}),
    UrlHelper: PropTypes.shape({}),
  }),
  showTaxCertLink: PropTypes.bool,
  taxCertLabel: PropTypes.string.isRequired,
  helpPopupTopic: PropTypes.string,
  additionalFormData: PropTypes.shape({}),
  isGlobalTaxForm: PropTypes.bool,
};

TaxCertificateFilePicker.defaultProps = {
  customStrings: {},
  certifications: [],
  mtTaxCertification: {},
  onCertificationsChange: _.noop,
  taxFormMode: TaxFormMode.EditMode,
  showLineByLineFormGroup: false,
  alwaysShowError: false,
  disabled: false,
  required: false,
  urlBuilder: {
    UrlBuilders: {},
    UrlHelper: {},
  },
  showTaxCertLink: false,
  helpPopupTopic: undefined,
  additionalFormData: {},
  isGlobalTaxForm: false,
};
